import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { LegacyStatus } from '@vendasta/notifications-sdk';
import { filter, take } from 'rxjs/operators';
import { VaDialogService } from '../../../common/va-dialog.service';
import { PartnerService } from '../../../core/partner.service';
import {
  DAILY_DIGEST_TYPE,
  EXECUTIVE_REPORT_TYPE,
  GLOBAL_SETTINGS_TYPE,
  INSTANT_EMAIL_SECTION_TYPE,
} from '../../constants';
import {
  AppSettingSection,
  LegacyNotificationPreferenceService,
  ProductNotifications,
} from '../legacy-notification-preference.service';

const SORT_ORDER = [EXECUTIVE_REPORT_TYPE, INSTANT_EMAIL_SECTION_TYPE, DAILY_DIGEST_TYPE].reverse(); // Reversing so it orders it in the needed way for the sort function

export interface NotificationSection {
  sectionType: string;
  name: string;
  sections: AppSettingSection[];
}

@Component({
  selector: 'app-legacy-notification-settings',
  templateUrl: './legacy-notification-settings.component.html',
  styleUrls: ['./legacy-notification-settings.component.scss'],
})
export class LegacyNotificationSettingsComponent {
  @Input()
  partnerId: string;
  @Input()
  accountGroupId: string;
  @Input()
  marketId: string;
  @Input()
  userId: string;
  @Input()
  bulk: boolean;
  @Input()
  set product(product: ProductNotifications) {
    this._product = product;

    this.sectionsToDisplay = [];
    product.sections.forEach((section) => {
      const notifSection = this.sectionsToDisplay.find(
        (secToDisplay) => secToDisplay.sectionType === section.sectionType,
      );
      if (notifSection) {
        notifSection.sections.push(section);
      } else {
        this.sectionsToDisplay.push({
          sectionType: section.sectionType,
          name: this.convertSectionTypeToKey(section.sectionType),
          sections: [section],
        });
      }
    });
    this.sectionsToDisplay.sort(function (a: NotificationSection, b: NotificationSection): number {
      if (SORT_ORDER.indexOf(a.sectionType) * -1 < SORT_ORDER.indexOf(b.sectionType) * -1) {
        return -1;
      } else if (SORT_ORDER.indexOf(a.sectionType) * -1 > SORT_ORDER.indexOf(b.sectionType) * -1) {
        return 1;
      }
      return 0;
    });
  }
  _product: ProductNotifications;
  @Input()
  disabledSections: string[];

  @Output()
  changes = new EventEmitter<AppSettingSection>();

  @Input()
  set notifications(notifications: ProductNotifications[]) {
    notifications.forEach((notification) => {
      notification.sections.forEach((section) => {
        this.allSectionsInProduct.push(section);
      });
    });
  }

  sectionsToDisplay: NotificationSection[] = [];
  allSectionsInProduct: AppSettingSection[] = [];

  STATUS_UNSET = LegacyStatus.STATUS_UNSET;
  STATUS_DEFAULT = LegacyStatus.STATUS_DEFAULT;
  STATUS_ENABLED = LegacyStatus.STATUS_ENABLED;
  STATUS_DISABLED = LegacyStatus.STATUS_DISABLED;
  notificationEnabled$ = this.partnerService.partnerNotificationEnabled$;

  constructor(
    private accountGroupNotificationService: LegacyNotificationPreferenceService,
    private snackbarService: SnackbarService,
    private dialogService: VaDialogService,
    private partnerService: PartnerService,
  ) {}

  setToDefault(): void {
    this.dialogService
      .confirm(
        'MARKET_NOTIFICATIONS.CONFIRM_TITLE',
        'MARKET_NOTIFICATIONS.CONFIRM_SUBTITLE',
        'MARKET_NOTIFICATIONS.CONFIRM_BUTTON',
      )
      .pipe(
        filter((result) => result === 'yes'),
        take(1),
      )
      .subscribe(() => {
        this.accountGroupNotificationService
          .deleteSectionSetting(this.partnerId, this.marketId, this.userId, this.accountGroupId)
          .subscribe({
            next: () => {
              this.snackbarService.successSnack('MARKET_NOTIFICATIONS.SETTINGS_STATUS.UPDATE_TO_DEFAULT');
              this.allSectionsInProduct.forEach((section) => {
                section.value = LegacyStatus.STATUS_DEFAULT;
              });
            },
            error: (err) => {
              err.status === 404
                ? this.snackbarService.successSnack('MARKET_NOTIFICATIONS.SETTINGS_STATUS.ALREADY_UPDATED')
                : this.snackbarService.errorSnack('MARKET_NOTIFICATIONS.SETTINGS_STATUS.ERROR_OCCURRED');
            },
          });
      });
  }

  updateSetting(section: AppSettingSection, sectionName: string, status: LegacyStatus): void {
    if (this.bulk) {
      section.value = status;
      this.changes.next(section);
      return;
    }

    const sectionNameToUse = sectionName === GLOBAL_SETTINGS_TYPE ? section.sectionId : sectionName;

    let sectionToUpdate;
    const splitSections = section.sectionId.split(':');

    if (splitSections.length > 2) {
      sectionToUpdate = this.sectionsToDisplay
        .find((sectionToDisplay) => sectionToDisplay.sectionType === sectionName)
        .sections.find((notifSection) => notifSection.sectionId === `${splitSections[0]}:${splitSections[1]}`)
        .children.find((childrenSection) => childrenSection.sectionId === section.sectionId);
    } else {
      sectionToUpdate = this.sectionsToDisplay
        .find((sectionToDisplay) => sectionToDisplay.sectionType === sectionName)
        .sections.find((notifSection) => notifSection.sectionId === section.sectionId);
    }

    sectionToUpdate.value = status;
    this.accountGroupNotificationService
      .updateSectionSetting(
        sectionNameToUse,
        section.sectionId,
        status,
        this.partnerId,
        this.marketId,
        this.userId,
        this.accountGroupId,
      )
      .subscribe({
        next: () => {
          this.snackbarService.successSnack('Settings updated');
        },
        error: (err) => {
          console.error(err);
          this.snackbarService.errorSnack(`An error occurred while updating settings`);
        },
      });
  }

  private convertSectionTypeToKey(sectionType: string): string {
    if (sectionType === INSTANT_EMAIL_SECTION_TYPE) {
      return 'USER_NOTIFICATIONS.INSTANT_EMAIL';
    }
    if (sectionType === DAILY_DIGEST_TYPE) {
      return 'USER_NOTIFICATIONS.DAILY_DIGEST';
    }
    if (sectionType === EXECUTIVE_REPORT_TYPE) {
      return 'USER_NOTIFICATIONS.EXECUTIVE_REPORT';
    }
    return sectionType;
  }
}
